/**
 * /academy/progress for the TEF Canada track (D-109): the verdict (ready, almost, not yet), the
 * critical points first, the estimated NCLC and score of each ability against the goal, the
 * evolution of the sittings, the rank among the TEF learners of the platform (positions only)
 * and the complete PDF report. From TEF sittings only; every level is an estimate.
 */

import { useAcademyProgressRetrieve } from "@agencyos/api-client";
import { formatScore } from "@agencyos/core";
import { Badge, GlassCard, Skeleton } from "@agencyos/ui-web";
import { AlertTriangle, TrendingUp, Trophy } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { TEF } from "../ielts/exam";
import { ReportButton } from "../ProgressPage";
import { RankScoreCard } from "../RankScoreCard";
import { dateText } from "../shared";
import { SKILLS, isObject } from "../types";
import { CardTitle, PageHead, SectionTitle, SkillIcon } from "../ui";
import "./tef.css";

interface Row {
  score: number | null;
  nclc: number | null;
  gap: number | null;
  trend: string;
}

function num(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function read(value: unknown) {
  const data = isObject(value) ? value : {};
  const analysis = isObject(data.analysis) ? data.analysis : {};
  const skills = isObject(analysis.skills) ? analysis.skills : {};
  const rows: Record<string, Row> = {};
  for (const skill of SKILLS) {
    const row = isObject(skills[skill]) ? skills[skill] : {};
    rows[skill] = { score: num(row.score), nclc: num(row.nclc), gap: num(row.gap), trend: typeof row.trend === "string" ? row.trend : "unknown" };
  }
  const critical = (Array.isArray(analysis.critical) ? analysis.critical : []).filter(isObject).map((c) => String(c.message ?? "")).filter(Boolean);
  const evolution = (Array.isArray(data.evolution) ? data.evolution : []).filter(isObject).map((row) => ({
    at: String(row.at ?? ""),
    kind: String(row.kind ?? ""),
    mock: num(row.mock),
    skills: isObject(row.skills) ? row.skills : {},
  }));
  const rank = isObject(data.rank) && isObject(data.rank.platform) ? data.rank.platform : null;
  return {
    verdict: typeof analysis.verdict === "string" ? analysis.verdict : "not_ready",
    target: num(analysis.target),
    rows,
    critical,
    evolution,
    rank: rank ? { position: num(rank.position) ?? 0, total: num(rank.total) ?? 0, top: num(rank.top_percent) ?? 100 } : null,
  };
}

const VERDICT_TONE: Record<string, "success" | "warning" | "danger"> = { ready: "success", almost: "warning", not_ready: "danger" };

export function TefProgressPage() {
  const { t, i18n } = useTranslation();
  const query = useAcademyProgressRetrieve({ exam: TEF }, { query: { retry: false } });
  const data = useMemo(() => read(query.data?.status === 200 ? query.data.data : null), [query.data]);
  if (query.isLoading) return <Skeleton style={{ height: 320 }} />;

  return (
    <div className="ac-page ac-tef">
      <PageHead eyebrow={`${t("ielts.exams.tef_canada.name")} · ${t("academy.progress.eyebrow")}`} title={t("tef.progress.title")} subtitle={<p className="ac-note">{t("tef.estimateLong")}</p>} />
      <GlassCard as="section" className="ac-card" aria-labelledby="tef-verdict">
        <CardTitle id="tef-verdict" icon={<TrendingUp size={16} />} tone="info">
          {t(`tef.progress.verdict.${data.verdict}`)}
        </CardTitle>
        <Badge tone={VERDICT_TONE[data.verdict] ?? "danger"}>{data.target !== null ? t("tef.progress.goal", { n: data.target }) : t("tef.estimate")}</Badge>
        {data.critical.length > 0 && (
          <ol className="ac-critical">
            {data.critical.map((message, index) => (
              <li key={index}>
                <AlertTriangle aria-hidden size={14} /> {message}
              </li>
            ))}
          </ol>
        )}
      </GlassCard>

      <section className="ac-section" aria-labelledby="tef-skills">
        <SectionTitle id="tef-skills">{t("tef.home.levels")}</SectionTitle>
        <ul className="ac-tef-levels">
          {SKILLS.map((skill) => {
            const row = data.rows[skill];
            return (
              <li key={skill} className={`ac-tef-level ac-surface ac-skill--${skill}`}>
                <SkillIcon skill={skill} size={16} />
                <span className="ac-tef-level__name">{t(`tef.skill.${skill}`)}</span>
                <span className="ac-tef-level__value ao-num">{row?.nclc ? t("tef.nclc", { n: row.nclc }) : "—"}</span>
                <span className="ac-caption ao-num">{formatScore(row?.score ?? null)}</span>
                {/* D-123: a level above the goal showed « -2 niveaux à gagner » */}
                {typeof row?.gap === "number" && row.gap > 0 ? (
                  <span className="ac-caption">{t("tef.progress.gap", { count: row.gap })}</span>
                ) : typeof row?.gap === "number" && row.score !== null ? (
                  <span className="ac-caption">{t("academy.progress.gapReached")}</span>
                ) : null}
              </li>
            );
          })}
        </ul>
      </section>

      <section className="ac-section" aria-labelledby="tef-evolution">
        <SectionTitle id="tef-evolution">{t("tef.progress.evolution")}</SectionTitle>
        {data.evolution.length ? (
          <div className="ac-table-wrap">
            <table className="ac-criteria">
              <thead>
                <tr>
                  <th scope="col">{t("tef.progress.date")}</th>
                  <th scope="col">{t("tef.progress.sitting")}</th>
                  {SKILLS.map((skill) => (
                    <th key={skill} scope="col" className="num">
                      {t(`tef.skillShort.${skill}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.evolution.map((row, index) => (
                  <tr key={index}>
                    <td>{row.at ? dateText(row.at, i18n.language) : "—"}</td>
                    <td>
                      {t(`academy.kind.${row.kind}`)}
                      {row.mock !== null ? ` n° ${row.mock}` : ""}
                    </td>
                    {SKILLS.map((skill) => {
                      const cell = isObject(row.skills[skill]) ? (row.skills[skill] as Record<string, unknown>) : null;
                      return (
                        <td key={skill} className="num ao-num">
                          {cell && typeof cell.nclc === "number" ? `${cell.nclc}` : "—"}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="ac-secondary">{t("tef.progress.none")}</p>
        )}
      </section>

      {data.rank && (
        <GlassCard as="section" className="ac-card" aria-labelledby="tef-rank">
          <CardTitle id="tef-rank" icon={<Trophy size={16} />} tone="warning">
            {t("tef.progress.rank", { position: data.rank.position, total: data.rank.total, top: data.rank.top })}
          </CardTitle>
        </GlassCard>
      )}
      <RankScoreCard id="tef-rank-score" rank={query.data?.status === 200 ? query.data.data.rank : null} />
      <ReportButton exam="tef_canada" />
    </div>
  );
}
